import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import {type ReactNode, useId, useState} from "react";
import {useTranslation} from "react-i18next";
import CNumberField from "crqlar-react-common/dist/components/CNumberField";
import {MultiSelect, SingleSelect} from "../../newSelect";
import {isServerTableRowsCustomValid, type ServerTableRowsMode, type ServerTableRowsValue} from "../rows/ServerTableRowsPicker";
import type {ServerTableExportColumn, ServerTableExportFormat, ServerTableExportRun} from "./serverTableExport";

interface ServerTableExportDialogProps {
    open: boolean
    onClose: () => void
    /** The list's title, as in "Export {{title}}". */
    title: string
    /** Every column the picker offers, from `useServerTableExport`. */
    columns: ServerTableExportColumn[]
    /** The picker's initial columns: the visible ones in display order. */
    visibleColumnIds: string[]
    pageRowCount: number
    /** The list rows before the loaded page: where "This page" starts. */
    pageOffset: number
    totalRowCount: number
    /**
     * A list's own options, between Columns and Rows, such as the survey export's "Include sensitive answers". The
     * caller owns their state.
     */
    extraOptions?: ReactNode
    /** Starts the export; the dialog has already closed. No `maxRows` for every row; an `offset` only for this page. */
    onExport: (format: ServerTableExportFormat, run: ServerTableExportRun & {columns: ServerTableExportColumn[]}) => void
}

const DEFAULT_CUSTOM_ROWS = 1000;

/**
 * What a list export writes: the format, the columns and which rows - the loaded page, a number from the top, or
 * all. Export closes the dialog at once; the face's button shows the running export. Opens with the defaults every
 * time.
 */
export function ServerTableExportDialog({open, onClose, ...formProps}: ServerTableExportDialogProps) {
    const titleId = useId();

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={titleId}
                PaperProps={{sx: {maxWidth: 520}}}>
            <ServerTableExportForm titleId={titleId} onClose={onClose} {...formProps}/>
        </Dialog>
    );
}

// Its own component, so the Dialog's unmount on close resets the choices.
function ServerTableExportForm({
    titleId,
    onClose,
    title,
    columns,
    visibleColumnIds,
    pageRowCount,
    pageOffset,
    totalRowCount,
    extraOptions,
    onExport,
}: Omit<ServerTableExportDialogProps, "open"> & {titleId: string}) {
    const {t} = useTranslation();
    const [format, setFormat] = useState<ServerTableExportFormat>("xlsx");
    const [columnIds, setColumnIds] = useState(visibleColumnIds);
    const [rowsValue, setRowsValue] = useState<ServerTableRowsValue>({
        mode: "page",
        custom: Math.min(DEFAULT_CUSTOM_ROWS, totalRowCount),
    });
    const rows = rowsValue.mode;
    const customRows = rowsValue.custom;

    const pickedColumns = columnIds
        .map(id => columns.find(column => column.id === id))
        .filter((column): column is ServerTableExportColumn => Boolean(column));
    const customValid = isServerTableRowsCustomValid(customRows);
    const rowCount = rows === "page"
        ? pageRowCount
        : rows === "custom" && customValid ? Math.min(customRows, totalRowCount) : undefined;
    const canExport = pickedColumns.length > 0 && (rows !== "custom" || customValid);
    // The button names what the file will hold: the page's rows, the custom count or the whole filtered list.
    const exportCount = rows === "all" ? totalRowCount : rowCount;

    const exportFile = () => {
        if (!canExport) return;
        onClose();
        onExport(format, {
            maxRows: rows === "all" ? undefined : rowCount,
            offset: rows === "page" ? pageOffset : undefined,
            columns: pickedColumns,
        });
    };

    return (
        <>
            <DialogTitle id={titleId}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    {t("Export {{title}}", {title})}
                    <IconButton size="small" aria-label={t("Close")} onClick={onClose}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Stack gap={2.5} sx={{pt: 1}}>
                    <SingleSelect
                        label={t("Format")}
                        value={format}
                        options={[
                            {value: "xlsx", label: t("Excel (.xlsx)")},
                            {value: "csv", label: t("CSV (.csv)")},
                        ]}
                        onChange={value => {
                            if (value === "xlsx" || value === "csv") setFormat(value);
                        }}
                        disableClearable
                    />

                    <Stack gap={0.5}>
                        <MultiSelect
                            label={t("Columns")}
                            value={columnIds}
                            options={columns.map(column => ({value: column.id, label: column.label}))}
                            onChange={setColumnIds}
                            bulkSelect
                        />
                        {!pickedColumns.length && <FormHelperText error>{t("Select at least one column.")}</FormHelperText>}
                    </Stack>

                    {extraOptions}

                    <SingleSelect
                        label={t("Rows")}
                        value={rows}
                        options={[
                            {value: "page", label: t("This page"), detail: t("{{count, number}} rows", {count: pageRowCount})},
                            {value: "custom", label: t("Custom"), detail: t("Pick a number")},
                            {value: "all", label: t("All"), detail: t("{{count, number}} rows", {count: totalRowCount})},
                        ]}
                        onChange={value => {
                            if (value === "page" || value === "custom" || value === "all") {
                                setRowsValue({...rowsValue, mode: value as ServerTableRowsMode});
                            }
                        }}
                        disableClearable
                    />

                    {rows === "custom" && (
                        <Stack gap={0.5}>
                            <CNumberField
                                label={t("Number of rows")}
                                value={customRows ?? undefined}
                                onChange={custom => setRowsValue({...rowsValue, custom})}
                                min={1}
                                max={Math.max(totalRowCount, 1)}
                                step={100}
                                size="small"
                                fullWidth
                            />
                            {!customValid && <FormHelperText error>{t("Enter a number of rows.")}</FormHelperText>}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose}>{t("Cancel")}</Button>
                <Button
                    variant="contained"
                    startIcon={<FileDownloadOutlinedIcon/>}
                    disabled={!canExport}
                    onClick={exportFile}
                >
                    {exportCount === undefined
                        ? t("Export")
                        : exportCount === 1
                            ? t("Export 1 row")
                            : t("Export {{count, number}} rows", {count: exportCount})}
                </Button>
            </DialogActions>
        </>
    );
}
